import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PokemonCard from '../components/PokemonCard';
import SmartSearchBar from '../components/SmartSearchBar';
import { PageContainer, SectionHeader } from '../components/shared';
import { 
  ChevronDown, 
  Loader2, 
  RefreshCw, 
  AlertCircle,
  Search,
  Grid3X3,
  List,
  SortAsc,
  SortDesc,
  ArrowLeft
} from 'lucide-react';

import { useLanguage } from '../contexts/LanguageContext';
import { getApiBaseUrl } from '../utils/apiUrl';

const SearchPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  
  const [pokemon, setPokemon] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchPokemon = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const { page = 1, search = query, sortBy = 'id', sortOrder = 'asc', limit = 12 } = params;
      const response = await fetch(`${getApiBaseUrl()}/api/pokemon?page=${page}&search=${search}&sortBy=${sortBy}&sortOrder=${sortOrder}&limit=${limit}`);
      const data = await response.json();
      
      if (data.success) {
        setPokemon(data.data);
        setPagination(data.pagination);
      } else {
        setError(data.error || 'Failed to fetch Pokemon');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    if (query) {
      fetchPokemon({ search: query });
    }
  }, [query, fetchPokemon]);

  const handleSearch = useCallback((searchQuery) => {
    navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
  }, [navigate]);

  const handleSort = useCallback((newSortBy) => {
    if (sortBy === newSortBy) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  }, [sortBy]);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await fetchPokemon({ search: query, sortBy, sortOrder });
    } catch (error) {
      console.error('Failed to refresh:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [query, sortBy, sortOrder, fetchPokemon]);

  const handleLoadMore = useCallback(() => {
    if (pagination && pagination.hasNextPage) {
      fetchPokemon({ 
        page: pagination.currentPage + 1,
        limit: 12,
        sortBy,
        sortOrder,
        search: query
      });
    }
  }, [pagination, sortBy, sortOrder, query, fetchPokemon]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-lg mx-auto p-8 bg-white rounded-2xl shadow-xl"
        >
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">{t('home.errorTitle')}</h2>
          <p className="text-red-600 mb-2 font-medium">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            {t('common.refreshPage')}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <PageContainer maxWidth="7xl" padding={true} className="py-8">
        <div className="space-y-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4 mb-8"
          >
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-white/50 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600" />
            </button>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                {t('home.featured')}
              </h1>
              <p className="text-gray-600">
                {query ? `"${query}"` : t('home.search.placeholder')}
              </p>
            </div>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-2xl mx-auto px-4"
          >
            <SmartSearchBar 
              placeholder={t('home.search.placeholder')}
              className="w-full"
            />
          </motion.div>

          {/* Controls */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-6 shadow-pokemon"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center gap-4">
                <h2 className="text-2xl font-bold text-gray-800">{t('home.featured')}</h2>
                {pagination && (
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {pagination.totalItems}
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-3">
                {/* View Mode Toggle */}
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'
                    }`}
                  >
                    <Grid3X3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${
                      viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500'
                    }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
                
                {/* Sort Options */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleSort('id')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      sortBy === 'id' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    ID
                    {sortBy === 'id' && (
                      sortOrder === 'asc' ? <SortAsc className="w-3 h-3 ml-1" /> : <SortDesc className="w-3 h-3 ml-1" />
                    )}
                  </button>
                  <button
                    onClick={() => handleSort('name_en')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      sortBy === 'name_en' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {t('home.sortName')}
                    {sortBy === 'name_en' && (
                      sortOrder === 'asc' ? <SortAsc className="w-3 h-3 ml-1" /> : <SortDesc className="w-3 h-3 ml-1" />
                    )}
                  </button>
                  <button
                    onClick={() => handleSort('max_cp')}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      sortBy === 'max_cp' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    CP
                    {sortBy === 'max_cp' && (
                      sortOrder === 'asc' ? <SortAsc className="w-3 h-3 ml-1" /> : <SortDesc className="w-3 h-3 ml-1" />
                    )}
                  </button>
                </div>
                
                {/* Refresh Button */}
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </motion.div>

          {/* Results */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-8"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="bg-white rounded-2xl p-6 shadow-lg"
                      >
                        <div className="h-48 bg-gradient-to-br from-gray-200 to-gray-300 rounded-xl mb-4 animate-pulse"></div>
                        <div className="space-y-3">
                          <div className="h-6 bg-gray-200 rounded-lg animate-pulse"></div>
                          <div className="h-4 bg-gray-200 rounded-lg w-3/4 animate-pulse"></div>
                          <div className="flex gap-2">
                            <div className="h-6 bg-gray-200 rounded-full w-16 animate-pulse"></div>
                            <div className="h-6 bg-gray-200 rounded-full w-20 animate-pulse"></div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={`grid gap-6 ${
                    viewMode === 'grid' 
                      ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                      : 'grid-cols-1'
                  }`}
                >
                  <AnimatePresence>
                    {pokemon && Array.isArray(pokemon) && pokemon.length > 0 ? pokemon.map((pokemon, index) => (
                      <motion.div
                        key={pokemon.Pokemon_ID}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05 }}
                        layout
                      >
                        <PokemonCard 
                          pokemon={pokemon} 
                          index={index} 
                          viewMode={viewMode}
                        />
                      </motion.div>
                    )) : (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="col-span-full text-center py-16"
                      >
                        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                          <Search className="w-12 h-12 text-gray-400" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-600 mb-2">
                          {query ? `${t('home.noResults')} "${query}"` : t('home.search.placeholder')}
                        </h3>
                        <p className="text-gray-500 mb-6">{t('home.noResultsHint')}</p>
                        <button
                          onClick={handleRefresh}
                          className="btn-primary"
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          {t('home.refresh')}
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Load More Button */}
            <AnimatePresence>
              {pagination && pagination.hasNextPage && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-center mt-8"
                >
                  <button
                    onClick={handleLoadMore}
                    disabled={loading}
                    className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed min-w-[200px]"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-5 h-5 mr-2" />
                        {t('home.loadMore')}
                      </>
                    )}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </PageContainer>
    </div>
  );
};

export default SearchPage;
